import type { RoundVerificationStates } from '../fair-play/fair-play.schema';
import type { PaginationType } from '../types/Pagination.types';
import z from 'zod';
import { CategoryDtoSchema } from '../categories/categories.schema';
import { GameAssetDtoSchema, MediaDtoSchema } from '../media/media.schema';
import { ProviderDtoSchema } from '../providers/providers.schema';
import { PageContentDtoSchema } from '../system-pages';
import { createUpdateZodSchema } from '../types/DateUtils.types';
import { PaginationParamsSchema } from '../types/Pagination.types';
import {
  GAME_BET_LEVEL_OPTIONS,
  GAME_JACKPOT_TYPE_OPTIONS,
  GAME_PROMO_TOOL_OPTIONS,
  GAME_RTP_TYPE_OPTIONS,
  GAME_SUB_JACKPOT_TYPE_OPTIONS,
  GAME_TOP_PRIZE_OPTIONS,
  GAME_VOLATILITY_OPTIONS,
} from './game-details.enum';

export const GameRtpSchema = z.preprocess(
  value => value === null
    ? undefined
    : typeof value === 'number'
      ? String(value)
      : value,
  z.string().optional(),
);

// Backend sometimes sends a single string instead of an array for these fields.
function lenientStringArray() {
  return z.preprocess(
    value => typeof value === 'string' ? [value] : value,
    z.array(z.string()).optional(),
  );
}

function lenientEnumArray<T extends string>(options: readonly T[]) {
  return z.preprocess(
    value => typeof value === 'string' ? [value] : value,
    z.array(z.string()).optional(),
  ).transform(values => values?.filter((value): value is T => (options as readonly string[]).includes(value)));
}

// Drop unknown/legacy values instead of failing the whole game payload.
function lenientEnum<T extends string>(options: readonly T[]) {
  return z.preprocess(
    value => typeof value === 'string' && (options as readonly string[]).includes(value) ? value : undefined,
    z.enum(options as unknown as [T, ...T[]]).optional(),
  );
}

export const PartnerDtoSchema = z.object({
  id: z.string().uuid(),
  partnerId: z.string(),
  partnerCode: z.string(),
  isActive: z.boolean(),
}).merge(createUpdateZodSchema);

export type Partner = z.infer<typeof PartnerDtoSchema>;

export const GameDetailsDtoSchema = z.object({
  category: lenientStringArray(),
  subCategory: lenientStringArray(),
  volatility: lenientEnumArray(GAME_VOLATILITY_OPTIONS),
  rtpType: z.enum(GAME_RTP_TYPE_OPTIONS).optional(),
  rtpRange: z.string().optional(),
  rtp: z.number().nullable().optional(),
  topPrize: z.enum(GAME_TOP_PRIZE_OPTIONS).optional(),
  topPrizeRange: z.string().optional(),
  features: lenientStringArray(),
  subFeature: lenientStringArray(),
  gameMechanics: lenientStringArray(),
  subGameMechanics: lenientStringArray(),
  subSubGameMechanics: lenientStringArray(),
  grid: lenientStringArray(),
  themes: lenientStringArray(),
  subThemes: lenientStringArray(),
  minBet: lenientEnum(GAME_BET_LEVEL_OPTIONS),
  minBetRange: z.string().optional(),
  maxBet: lenientEnum(GAME_BET_LEVEL_OPTIONS),
  maxBetRange: z.string().optional(),
  maxBetNumber: z.number().optional(),
  maxMultiplier: z.number().optional(),
  maxWin: z.number().optional(),
  promoTools: lenientEnumArray(GAME_PROMO_TOOL_OPTIONS),
  jackpotType: z.enum(GAME_JACKPOT_TYPE_OPTIONS).optional(),
  subJackpotType: z.enum(GAME_SUB_JACKPOT_TYPE_OPTIONS).optional(),
}).describe('GameDetailsDtoSchema');

export const GameDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string()
    .nullable(),
  gameId: z.string(), // RELAX id, we will not use it

  alias: z.string(), // alias, slug for url
  order: z.number(),

  isActive: z.boolean(),
  isBonusAllowed: z.boolean().optional().default(true),
  cover: MediaDtoSchema.nullable(),
  settings: z.object({
    allow: z.string().optional(),
  }).nullable(),
  categories: z.array(CategoryDtoSchema),
  provider: ProviderDtoSchema.nullish(),
  partner: PartnerDtoSchema.nullish(),
  isFavorite: z.boolean().optional(),
  // field for seo game gallery
  assets: z.array(GameAssetDtoSchema).nullish(),

  pageContent: PageContentDtoSchema.nullish(),

  details: GameDetailsDtoSchema.optional().nullable(),

}).merge(createUpdateZodSchema).describe('GameDtoSchema');

// INFO: This dto refers to UserGameListItemDto on BE side
export const GameWithWinsDtoSchema = GameDtoSchema
  .omit({
    order: true,
    description: true,
    gameId: true,
    settings: true,
    createdAt: true,
    categories: true,
  })
  .partial({
    isActive: true,
    updatedAt: true,
  })
  .describe('GameWithWinsDtoSchema');

export const GameListItemDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  alias: z.string(),
  cover: MediaDtoSchema.nullish().transform(v => v ?? null),
  isFavourite: z.boolean().optional().default(false),
}).describe('GameListItemDtoSchema');

export const GameLaunchUrlPayloadSchema = z.object({
  gameId: z.string(),
  userId: z.string(),
  currency: z.string(),
  language: z.string(),
  country: z.string(),
  device: z.string(),
});

export const GameLaunchUrlResponseSchema = z.object({
  code: z.string(),
  url: z.string(),
});

// TODO: need to think how to make some of the filters more flexible, e.g., supporting multiple values or complex conditions
// and make them more consistent with the backend API
const GameStringFilterValueSchema = z.union([
  z.string(),
  z.object({
    $in: z.array(z.string()).optional(),
  }),
]);

export const GamesFiltersSchema = z.object({
  'name': z.string().optional(),
  'gameId': z.string().optional(),
  'gameCode': z.string().optional(),
  'alias': z.string().optional(),
  'isActive': z.boolean().optional(),
  'categoryId': z.string().optional(),
  'partnerId': z.string().optional(),
  'details.category': GameStringFilterValueSchema.optional(),
  'details.volatility': GameStringFilterValueSchema.optional(),
  'details.rtpType': GameStringFilterValueSchema.optional(),
  'details.features': GameStringFilterValueSchema.optional(),
  'details.gameMechanics': GameStringFilterValueSchema.optional(),
  'details.themes': GameStringFilterValueSchema.optional(),
  'provider.alias': GameStringFilterValueSchema.optional(),
  'providerId': z.union([
    z.string(),
    z.object({
      $in: z.array(z.string()).optional(),
    }),
  ]).optional(),
}).optional();

export const GamesParamsSchema = PaginationParamsSchema(GamesFiltersSchema);

export type GamesFilters = z.infer<typeof GamesFiltersSchema>;
export type GamesFiltersParamsPayload = z.infer<typeof GamesParamsSchema>;

export const SharedGameSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  alias: z.string(),
  cover: MediaDtoSchema.nullable(),
});

export const GamesWithWinsFiltersSchema = z.object({
  name: z.string().optional(),
}).optional();

export const GamesWithWinsParamsSchema = PaginationParamsSchema(GamesWithWinsFiltersSchema);
export type GamesWithWinsParams = z.infer<typeof GamesWithWinsParamsSchema>;
export type GameDto = z.infer<typeof GameDtoSchema>;
export type GameWithWinsDto = z.infer<typeof GameWithWinsDtoSchema>;
export type GameListItemDto = z.infer<typeof GameListItemDtoSchema>;
export type GameListDto = PaginationType<GameListItemDto>;
export type GameLaunchUrlPayload = z.infer<typeof GameLaunchUrlPayloadSchema>;
export type GameLaunchUrlResponse = z.infer<typeof GameLaunchUrlResponseSchema>;
export type GameDetailsDto = z.infer<typeof GameDetailsDtoSchema>;
export type RoundVerificationStatesType = (typeof RoundVerificationStates)[keyof typeof RoundVerificationStates];
